import React from 'react';
import { Link } from 'react-router-dom';
import './rush.css';
import './about.css';
import { RushNav, RushFooter, useRushReveal } from './RushChrome';
import chrisPhoto from './chris-rush.jpg';

const Hero = () => (
  <section className="about-hero">
    <div className="hero-mesh"></div>
    <div className="rs-container hero-anim" style={{ position: 'relative', zIndex: 1 }}>
      <span className="hero-eyebrow"><span className="dot"></span>About Rush Studio</span>
      <h1 className="about-h1">We're building the translation tool we wished existed when we were running localization ourselves.</h1>
      <p className="about-lead">Rush Studio is a small team of localization veterans, ex-translators, and software engineers — most of whom have worked across at least three languages, and all of whom have at some point cursed a CAT tool out loud.</p>
    </div>
  </section>
);

const Story = () => (
  <section className="section">
    <div className="rs-container about-story">
      <div className="about-story-meta reveal">
        <div className="eyebrow">The story</div>
        <h2 className="about-story-h">A letter from the founder.</h2>
        <div className="about-story-side">
          <div className="about-story-signers">
            <div className="about-signer">
              <div className="about-signer-avatar" style={{ width: 84, height: 84, flexShrink: 0 }}>
                <img src={chrisPhoto} alt="Chris Rush" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', borderRadius: '50%' }} />
              </div>
              <div>
                <b>Chris Rush</b>
                <span>Founder</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="about-story-body reveal">
        <p className="about-story-lede">I started Rush Studio in 2024, after watching a company I worked for hit the same wall I'd run into again and again.</p>
        <p>We'd stopped using translation agencies — they were simply too expensive — and moved to a machine-translation platform instead. It saved money, but it created a new problem: we couldn't find a single platform that could properly translate our IDML files.</p>
        <p>That broke the whole workflow. Files went out in one shape and came back in another. The formatting our designers had carefully set up was gone, and someone had to rebuild it by hand, every single time.</p>
        <p>So I decided to build my own platform — one that was <span className="highlight">transparent, easy to use, and respected the files teams actually work with.</span> Something that improved the overall workflow instead of adding steps to it.</p>
        <p>The goal was simple: save teams a huge amount of time and money, without asking them to give up control or quality. Translation should be a workflow that gets out of your way — not another tool to fight.</p>
        <p>Rush Studio is still early, and still being shaped by the people testing it. If that sounds like a tool you've been missing — <Link to="/register">come help build it</Link>.</p>
      </div>
    </div>
  </section>
);

const VALUES = [
  {
    title: 'Languages are not a commodity.',
    desc: "Every language has its own rhythm, its own register, its own idioms. We refuse to treat translation as a single 'service' that swaps interchangeable words. Every feature we ship has to respect that.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M3 6h12M9 4v2M5 6c0 5 3 9 8 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M13 22l5-12 5 12M14.5 18h7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: 'Software should get out of the way.',
    desc: 'The best tool is the one your translators stop noticing. We obsess over keyboard shortcuts, instant feedback, and removing clicks. If a feature needs a tooltip to explain it, we usually delete the feature.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M4 5h16M4 12h10M4 19h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M17 8l5 4-5 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: 'AI helps. Humans decide.',
    desc: "We use machine translation, and we'll tell you exactly where. We will never pretend an AI draft is a finished translation, and we will never bury a human reviewer beneath a UI built to look like a tool that doesn't need them.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <circle cx="8" cy="9" r="3.5" stroke="currentColor" strokeWidth="1.8" />
        <path d="M3 21c1-3.5 3-5 5-5s4 1.5 5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M17 7l2.4 1.4L22 7M19.4 8.4V12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="19.5" cy="15.5" r="2" stroke="currentColor" strokeWidth="1.8" />
      </svg>
    ),
  },
  {
    title: 'Build for the next decade.',
    desc: 'Translation tools that look modern this year will look dated next year. We design around the durable parts of the workflow — the things every localization team has done for thirty years and will still do in thirty more.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" />
        <path d="M3 9h18M8 13h3M8 16h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
  },
];

const Values = () => (
  <section className="section" style={{ background: 'var(--bg-soft)', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }}>
    <div className="rs-container">
      <div className="section-head reveal">
        <div className="eyebrow">What we believe</div>
        <h2>Four things we keep coming back to.</h2>
        <p className="section-lead">When the work gets ambiguous — and it always does — these are the principles that decide.</p>
      </div>
      <div className="values-grid reveal stagger">
        {VALUES.map((v) => (
          <article className="value-card" key={v.title}>
            <div className="value-icon">{v.icon}</div>
            <h3 className="value-title">{v.title}</h3>
            <p className="value-desc">{v.desc}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const Backers = () => (
  <section className="section-tight" style={{ borderTop: '1px solid var(--line)' }}>
    <div className="rs-container">
      <div className="backers-row reveal">
        <div className="backers-label">
          <div className="eyebrow">Backed by</div>
          <p>Investors who've built and operated localization businesses.</p>
        </div>
        <div className="backers-logos">
          <div className="backer-name" style={{ fontFamily: 'Georgia, serif', fontSize: 22, fontStyle: 'italic' }}>Loftwork</div>
          <div className="backer-name" style={{ letterSpacing: '0.22em', fontWeight: 700, fontSize: 14 }}>NORDPATH</div>
          <div className="backer-name" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, letterSpacing: '-0.03em', fontSize: 22 }}>Hexa/Capital</div>
          <div className="backer-name" style={{ fontWeight: 600, fontSize: 18 }}>Common Tongue</div>
          <div className="backer-name" style={{ fontFamily: 'Georgia, serif', fontSize: 20 }}>Reseda&nbsp;Ventures</div>
        </div>
      </div>
    </div>
  </section>
);

const MILESTONES = [
  { year: '2024', title: 'Brainstorming solutions and opportunities', desc: 'The idea takes shape — a transparent, easy-to-use translation platform built around the way teams actually work.' },
  { year: '2024', title: 'First prototype', desc: 'Started to build a prototype website to test the core workflow end to end.' },
  { year: '2025', title: 'Real-world testing', desc: 'Starting testing with real data and building out new features to improve UI/UX.' },
  { year: '2025', title: 'A fresh look', desc: 'Complete UI design change, shaped by everything we learned from early testing.' },
  { year: '2026', title: 'Heads-down build', desc: 'Finalising coding before beta testing.' },
  { year: '2027', title: 'Launch', desc: 'Launching Rush Studio to the world.' },
];

const Timeline = () => (
  <section className="section" style={{ background: 'var(--bg-soft)', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }}>
    <div className="rs-container">
      <div className="section-head reveal" style={{ textAlign: 'left', maxWidth: 'none', margin: '0 0 40px' }}>
        <div className="eyebrow">The journey</div>
        <h2 style={{ fontSize: 36 }}>How it's coming together.</h2>
      </div>
      <div className="timeline reveal">
        {MILESTONES.map((m, i) => (
          <div className="timeline-row" key={i}>
            <div className="timeline-year">{m.year}</div>
            <div className="timeline-marker">
              <span className="timeline-dot"></span>
              {i < MILESTONES.length - 1 && <span className="timeline-line"></span>}
            </div>
            <div className="timeline-body">
              <div className="timeline-title">{m.title}</div>
              <div className="timeline-desc">{m.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Numbers = () => (
  <section className="section-tight">
    <div className="rs-container">
      <div className="about-stats reveal stagger">
        <div className="about-stat"><div className="about-stat-num">2024</div><div className="about-stat-label">Year founded</div></div>
        <div className="about-stat"><div className="about-stat-num">2027</div><div className="about-stat-label">Launching</div></div>
        <div className="about-stat"><div className="about-stat-num">100<span className="accent">+</span></div><div className="about-stat-label">Languages supported</div></div>
        <div className="about-stat"><div className="about-stat-num">1</div><div className="about-stat-label">Founder, building it</div></div>
      </div>
    </div>
  </section>
);

const RushAboutPage = () => {
  useRushReveal();
  return (
    <div className="rs">
      <RushNav />
      <Hero />
      <Story />
      <Values />
      <Backers />
      <Timeline />
      <Numbers />
      <RushFooter />
    </div>
  );
};

export default RushAboutPage;
